import React, { FC, PropsWithChildren, useMemo } from 'react';
import styled from 'styled-components';
import * as DataTableTypes from './utils/DataTableTypes';
import { calculateTableWrapperHeight, getDataTableHeight } from './utils/getDataTableHeight';
import { usePluginContext } from './plugins/impl';
import { useDataTableContext } from './utils/DataTableContext';
import { usePropsForwarderContext } from './plugins/impl/context/ForwarderContext';

interface Props {
  className: string | undefined;
  tableHeight: 'content' | string;
  rowHeight: DataTableTypes.DataTableRowHeight | undefined;
  dataLength: number;
  limit: number;
  reservePaginatorSpaceForSinglePage: boolean;
}

// The paginator renders nothing for a single page, so its space is only needed when there are several.
const getMinPageCount = (reservePaginatorSpaceForSinglePage: boolean) => (reservePaginatorSpaceForSinglePage ? 1 : 2);

export const DataTableHeight: FC<PropsWithChildren<Props>> = ({
  dataLength,
  children,
  className,
  tableHeight,
  rowHeight,
  limit,
  reservePaginatorSpaceForSinglePage
}) => {
  const isToolbarVisible = usePluginContext().isDestinationDefined('toolbar');
  const hasQuickReports = usePropsForwarderContext().isEnabled('quickReports');
  const { getPageOptions, isFetching } = useDataTableContext();
  const tablePageCount = getPageOptions?.()?.length ?? 0;
  const calculatedTableHeight = useMemo(
    () =>
      calculateTableWrapperHeight({
        tableHeight,
        options: {
          rowHeight,
          isQuickReportsEnabled: hasQuickReports,
          dataLength,
          hasPagination: tablePageCount >= getMinPageCount(reservePaginatorSpaceForSinglePage),
          hasToolbar: isToolbarVisible,
          isFetching,
          limit
        }
      }),
    [
      tableHeight,
      rowHeight,
      hasQuickReports,
      dataLength,
      tablePageCount,
      isToolbarVisible,
      isFetching,
      limit,
      reservePaginatorSpaceForSinglePage
    ]
  );
  return (
    <StyledTableHeight className={className} $height={calculatedTableHeight}>
      {children}
    </StyledTableHeight>
  );
};

export const DataTableWrapper: FC<React.PropsWithChildren<{ reservePaginatorSpaceForSinglePage: boolean }>> = ({
  children,
  reservePaginatorSpaceForSinglePage
}) => {
  const { getPageOptions, isFetching } = useDataTableContext();
  const hasQuickReports = usePropsForwarderContext().isEnabled('quickReports');
  const tablePageCount = getPageOptions?.()?.length ?? 0;
  const hasSecondaryToolbar = usePluginContext().isDestinationDefined('toolbar', 'below');
  const isToolbarVisible = usePluginContext().isDestinationDefined('toolbar');

  return (
    <StyledTableWrapper
      className="data-table-wrapper"
      $height={getDataTableHeight(
        tablePageCount >= getMinPageCount(reservePaginatorSpaceForSinglePage),
        isToolbarVisible,
        hasSecondaryToolbar,
        !!isFetching,
        hasQuickReports
      )}
    >
      {children}
    </StyledTableWrapper>
  );
};

const StyledTableHeight = styled.div<{ $height: string }>`
  height: ${({ $height }) => $height};
`;

const StyledTableWrapper = styled.div<{ $height?: string }>`
  height: ${({ $height }) => $height};
  width: 100%;
`;
